import { useAssignablePeople } from '@/features/identity';
import { useWorkspace } from '@/features/workspaces';

import { useWorkspaceMembers } from '../hooks/useWorkspaceMembers';
import { GeneralSection } from './GeneralSection';
import { NameRow } from './NameRow';
import { RemoveWorkspaceSection } from './RemoveWorkspaceSection';

/**
 * Settings → General (spec 227): the name, who can see it, and — last, apart,
 * and only for those who may — removing the workspace (spec 198).
 *
 * It reads what it shows rather than taking it from the layout: the same
 * workspace and members reads, under the same cache keys, so that is no second
 * request; and the tab owns its own counts, as the other tabs do.
 */
export function GeneralTab({ workspaceId }: { workspaceId: string }) {
  const { data: workspace } = useWorkspace(workspaceId);
  const access = workspace?.access;
  const canWork = access?.can_work ?? true;
  const canManage = access?.can_manage ?? true;
  const members = useWorkspaceMembers(
    workspaceId,
    workspace != null && canWork,
  );
  // The organization's size, for the Organization card: the directory the
  // add-member picker reads, cached for a minute. Only the cards show it, and
  // only someone who manages sees the cards.
  const organization = useAssignablePeople(undefined, {
    enabled: workspace != null && canManage,
  });
  if (!workspace) return null;

  const memberList = members.data?.members;
  const owners = memberList
    ?.filter((member) => member.role === 'owner' && member.is_active)
    .map((member) => member.user.display_name);

  return (
    <>
      <NameRow
        workspaceId={workspaceId}
        name={workspace.name}
        canManage={canManage}
      />
      <GeneralSection
        key={workspace.visibility ?? 'private'}
        workspaceId={workspaceId}
        name={workspace.name}
        visibility={workspace.visibility ?? 'private'}
        nonMemberFollowers={members.data?.non_member_followers ?? 0}
        memberCount={memberList?.length}
        organizationCount={
          organization.failed || organization.loading
            ? undefined
            : organization.people.length
        }
        owners={owners}
        canManage={canManage}
      />
      {canManage ? (
        <RemoveWorkspaceSection
          workspaceId={workspaceId}
          name={workspace.name}
        />
      ) : null}
    </>
  );
}
